import React from 'react';
import { FormattedMessage } from 'react-intl';
import {createColumnHelper, SortingFn} from "@tanstack/react-table";

import {getFilteredData} from './utils';
import {SearchResult} from "features/search/types";
import {ObjectStatus, ParkingStock} from "features/search/filters/enums";
import {getFormattedMessage} from "features/services/utils/language-helper";
import { selectIsMobile, useDeviceStore } from 'features/ui/store/useDeviceStore';
import {addDevCounter, renderAsCurrency, renderTotalPriceJmoy} from "./renderers";

import './style.scss';

import { P } from '@atoms/Text/P';
import {Table} from "@organisms/GeneralComponents/Table";
import {DataTableCardType} from "@organisms/GeneralComponents/Table/CardView";
import { getParkingStockType } from 'features/services/utils/name-helpers';
import { Tags, TagDesign } from '@atoms/Tags/Tags';
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

interface Props {
    objects: SearchResult[];
    parkingProject: boolean;
}

export const Parkings: React.FunctionComponent<Props> = ({ objects, parkingProject }) => {
    const isMobile = useDeviceStore(selectIsMobile);

    const filteredObjects = getFilteredData(objects, parkingProject)
    const filterByStockType = filteredObjects
        .filter((item) => item.objectStatus === ObjectStatus.FOR_SALE)
        .filter(
            (item, index, self) =>
                index === self.findIndex(
                (t) => t.carParkingStock === item.carParkingStock
            )
        );

    const sortObjectStatusFn: SortingFn<SearchResult> = (rowA, rowB, _columnId) => {
        const statusA = rowA.original.objectStatus;
        const statusB = rowB.original.objectStatus;
        const statusOrder = [ObjectStatus.FOR_SALE, ObjectStatus.RESERVED, ObjectStatus.BOOKED, ObjectStatus.SOLD];
        const sortValue = statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);

        return sortValue;
    }
    const sortStockTypeFn: SortingFn<SearchResult> = (rowA, rowB, _columnId) => {
        const statusA = rowA.original.carParkingStock;
        const statusB = rowB.original.carParkingStock;
        const statusOrder = [ParkingStock.A, ParkingStock.B, ParkingStock.C, ParkingStock.D, ParkingStock.E, ParkingStock.F];
        const sortValue = statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);

        return sortValue;
    }

    const columnHelper = createColumnHelper<SearchResult>();
    const columns = [
        columnHelper.accessor(`objectStatus`, {
            id: 'parking-status',
            cell: info => {return <Tags 
                                tagDesign={TagDesign.DOT} 
                                status={info.getValue()} 
                                className='status' 
                            />},
            header: () => addDevCounter(1) + getFormattedMessage('jm.components.Pricelist.status'),
            sortingFn: sortObjectStatusFn,
        }),
        columnHelper.accessor(`price`, {
            id: 'parking-price',
            cell: info => renderAsCurrency(info.getValue()?.toString(), info.row.original),
            header: () => addDevCounter(2) + getFormattedMessage('jm.components.Pricelist.price'),
            sortUndefined: 'last',
            sortingFn: "alphanumeric"
        }),
        columnHelper.accessor(`jmoyTotalPrice`, {
            id: 'parking-jmoyTotalPrice',
            cell: info => renderTotalPriceJmoy(info.getValue(), info.row.original),
            header: () => addDevCounter(3) + getFormattedMessage('jm.components.Pricelist.totalPrice'),
            sortUndefined: 'last',
            sortingFn: "alphanumeric"
        }),
        columnHelper.accessor(`fee`, {
            id: 'parking-fee',
            cell: info => renderAsCurrency(info.getValue(), info.row.original),
            header: () => addDevCounter(4) + getFormattedMessage('jm.components.Pricelist.fee'),
            sortUndefined: 'last',
            sortingFn: "alphanumeric"
        }),
        columnHelper.accessor(`apartmentNumber`, {
            id: 'parking-objectNumber',
            cell: info => info.getValue(),
            header: () => addDevCounter(5) + 'Parkkipaikka',
            sortUndefined: 'last',
            sortingFn: "alphanumeric"
        }),
        columnHelper.accessor(`carParkingStock`, {
            id: 'parking-shareType',
            cell: info => getParkingStockType(info.getValue()),
            header: () => addDevCounter(6) + 'Osake',
            sortUndefined: 'last',
            sortingFn: sortStockTypeFn
        }),
    ];

    if (objects && objects.length === 0 || !columns) return null;

    return (
        <div className="price-list">
            <div className="price-list__header">
                <Headline level={HeadlineLevel.H2} tabable>
                    {parkingProject
                        ? getFormattedMessage('jm.components.Parking.parkingBuilding')
                        : getFormattedMessage('jm.components.Parking.parkingSpaces')}
                </Headline>
                {!parkingProject && <P><FormattedMessage id="jm.components.Parking.explanation" /></P>}
                {!isMobile &&
                    <span aria-hidden="true" className='parking-statuses'>
                        <Tags 
                            tagDesign={TagDesign.DOT} 
                            status={ObjectStatus.FOR_SALE} 
                            className='status' 
                        />
                        {!parkingProject &&
                            <>
                                <Tags 
                                    tagDesign={TagDesign.DOT} 
                                    status={ObjectStatus.BOOKED} 
                                    className='status' 
                                />
                                <Tags 
                                    tagDesign={TagDesign.DOT} 
                                    status={ObjectStatus.SOLD} 
                                    className='status' 
                                />
                            </>
                        }
                    </span>
                }
            </div>
            <Table
                id="parking-table"
                data={parkingProject
                    ? filterByStockType
                    : filteredObjects
                }
                columns={columns}
                columnVisibility={{
                    'parking-objectNumber': !parkingProject,
                    'parking-shareType': parkingProject,
                }}
                foldableCount={parkingProject ? 6 : 5}
                cardType={parkingProject
                    ? DataTableCardType.PARKING_STOCK
                    : DataTableCardType.PARKING_SPOT
                }
                currentItem={''}
                currentObjectId={''}
                defaultSort={
                    parkingProject
                        ? 'parking-shareType'
                        : 'parking-status'
                }
                noHover
            />
        </div>
    );
};
